/*
 * slider-counter.js
 * Zamienia kropki nawigacyjne slidera na licznik "< X z Y >" — numer/liczba stron plus
 * chevrony-buttony przewijające (Embla scrollPrev/scrollNext), wygaszane na krańcach
 * (canScrollPrev/canScrollNext). Na mobile, gdzie motyw chowa strzałki, dają też tap-nawigację.
 * Generyczne, wielokrotnego użytku: działa dla KAŻDEGO <hdt-slider defer-init> w sklepie,
 * który ma klasę "nen-slider-counter". Wpięcie w kolejny slider = dodać tę klasę i
 * załadować ten plik (docelowo globalnie w theme.liquid).
 * Separator "X z Y" jest domyślnie polski ("z"); nadpisywalny per slider atrybutem
 * data-counter-sep (droga do i18n przy szerszym rolloncie). Odstęp wokół separatora
 * robi CSS gap, nie spacje (flexbox i tak zjada spacje na brzegach elementu).
 *
 * Skąd bierzemy stan: z API slidera (Embla). <hdt-slider> po inicjalizacji wysyła
 * zdarzenie "init:slide" z detail.sliderApi (instancja Embla). Z niej czytamy
 * selectedScrollSnap() (bieżąca strona, 0-based) i scrollSnapList().length (liczba stron),
 * a zdarzenie "select" odświeża licznik przy każdej zmianie strony. To źródło jest
 * niezależne od DOM-owej struktury kropek (klasy/owijki bywają różne między buildami),
 * więc odczyt jest jednoznaczny. "init:slide" nie bąbelkuje — słuchamy na elemencie
 * slidera. Atrybut defer-init sprawia, że slider inicjalizuje się PO tym skrypcie, więc
 * listener zdąży się podpiąć — także dla sliderów doładowanych leniwie przez Section
 * Rendering API (assets/lazy-sections.js), które wychwytujemy obserwatorem na body.
 *
 * Kropki są ukrywane CSS-em (wstrzykiwanym raz; selektor tylko na bezpośrednie dziecko,
 * by nie ruszać zagnieżdżonych sliderów), a w ich miejsce pokazujemy licznik — tylko
 * poniżej 1150px (tam, gdzie motyw i tak pokazywał kropki; na desktopie zostają strzałki).
 * Licznik chowa się, gdy strona jest tylko jedna (total <= 1).
 *
 * total = liczba stron slidera (Embla scrollSnapList). Jeśli dany slider przewija po
 * jednej karcie, stron będzie dużo — to konfiguracja slidera (slidesToScroll), nie tego
 * licznika; licznik wiernie odzwierciedla realne strony.
 */
(function () {
  'use strict';

  // Guard: plik może być wczytany przez kilka placeholderów/sekcji na jednej stronie.
  if (window.__hdtSliderCounter) return;
  window.__hdtSliderCounter = 1;

  var SEP_DEFAULT = 'z';                  // separator "X z Y" (per slider: data-counter-sep); odstępy robi gap
  var MARK = 'nen-slider-counter';        // opt-in: klasa na <hdt-slider>
  var ON = 'nen-slider-counter--active';  // dołączana, gdy licznik ma sens (total > 1)
  var BOX = 'nen-slider-counter__box';
  var WIRED = '__nenCounterWired';        // flaga idempotencji per slider
  var styleInjected = false;

  function injectStyle() {
    if (styleInjected) return;
    styleInjected = true;
    var css =
      '.' + MARK + ' > [hdt-slider-dots]{display:none!important}' +
      '.' + BOX + '{display:none;justify-content:center;align-items:center;gap:.34em;' +
      'margin-top:var(--dots-distance,15px);font-size:1.3rem;font-weight:400;line-height:1;' +
      'color:rgb(var(--color-foreground) / .7);font-variant-numeric:tabular-nums}' +
      '.nen-slider-counter__nav{display:inline-flex;align-items:center;justify-content:center;' +
      'width:1.9em;height:1.9em;padding:0;margin:0;border:none;background:none;color:inherit;' +
      'cursor:pointer;-webkit-appearance:none;appearance:none;transition:opacity .2s}' +
      '.nen-slider-counter__nav svg{width:.62em;height:auto;display:block}' +
      '.nen-slider-counter__nav[disabled]{opacity:.3;cursor:default}' +
      '@media(max-width:1149px){.' + ON + ' .' + BOX + '{display:flex}}';
    var el = document.createElement('style');
    el.setAttribute('data-slider-counter', '');
    el.textContent = css;
    document.head.appendChild(el);
  }

  function chevronBtn(dir) {
    var pts = dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    var label = dir === 'prev' ? 'Poprzednia strona' : 'Następna strona';
    return '<button type="button" class="nen-slider-counter__nav" data-dir="' + dir + '" aria-label="' + label + '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="' + pts + '"/></svg></button>';
  }

  function ensureCounter(slider) {
    var c = slider.querySelector(':scope > .' + BOX);
    if (c) return c;
    var sep = slider.getAttribute('data-counter-sep') || SEP_DEFAULT;
    c = document.createElement('div');
    c.className = BOX;
    c.setAttribute('aria-live', 'polite');
    c.innerHTML =
      chevronBtn('prev') +
      '<span class="nen-slider-counter__cur">1</span>' +
      '<span class="nen-slider-counter__sep">' + sep + '</span>' +
      '<span class="nen-slider-counter__total">1</span>' +
      chevronBtn('next');
    slider.appendChild(c);
    return c;
  }

  function wire(slider) {
    if (slider[WIRED]) return;
    slider[WIRED] = true;
    injectStyle();
    var counter = ensureCounter(slider);
    var curEl = counter.querySelector('.nen-slider-counter__cur');
    var totalEl = counter.querySelector('.nen-slider-counter__total');
    var prevBtn = counter.querySelector('.nen-slider-counter__nav[data-dir="prev"]');
    var nextBtn = counter.querySelector('.nen-slider-counter__nav[data-dir="next"]');
    var api = null;
    var bound = false;

    function render() {
      if (!api || typeof api.scrollSnapList !== 'function') return;
      var total = api.scrollSnapList().length;
      if (total <= 1) { slider.classList.remove(ON); return; }
      curEl.textContent = api.selectedScrollSnap() + 1;
      totalEl.textContent = total;
      prevBtn.disabled = !api.canScrollPrev();
      nextBtn.disabled = !api.canScrollNext();
      slider.classList.add(ON);
    }

    prevBtn.addEventListener('click', function () { if (api) api.scrollPrev(); });
    nextBtn.addEventListener('click', function () { if (api) api.scrollNext(); });

    // Embla API przychodzi w evencie init:slide (bubbles:false -> słuchamy na sliderze).
    // defer-init inicjalizuje slider po tym skrypcie, więc listener zdąży przed init.
    // Event leci też na reInit (ta sama instancja api) -> select/reInit rejestrujemy raz.
    slider.addEventListener('init:slide', function (e) {
      if (!e.detail || !e.detail.sliderApi) return;
      api = e.detail.sliderApi;
      render();
      if (!bound) {
        bound = true;
        api.on('select', render);
        api.on('reInit', render);
      }
    });
  }

  function scan(root) {
    if (root.nodeType !== 1) return;
    if (root.matches && root.matches('hdt-slider.' + MARK)) wire(root);
    if (root.querySelectorAll) {
      var found = root.querySelectorAll('hdt-slider.' + MARK);
      for (var i = 0; i < found.length; i++) wire(found[i]);
    }
  }

  function init() {
    scan(document.body);
    // Slidery doładowane później (SRA/lazy) — łapiemy wstrzyknięte poddrzewa.
    var bodyMo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) scan(added[j]);
      }
    });
    bodyMo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
